import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, UserRound, UserX } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

/** Lists every alumno assigned to one course/taller — opened from the
 * "Asignados" button in CourseListPage.jsx. Shows assignment date, last
 * exam-attempt date in that course (best available proxy for "last time they
 * entered"), a link to the student's profile, and — admin/superadmin only —
 * an unassign action. */
export default function CourseAssignmentsModal({ courseId, courseTitle, open, onClose, onChanged }) {
  const { user: currentUser } = useAuth();
  const canUnassign = currentUser?.role === "admin" || currentUser?.role === "superadmin";
  const [rows, setRows] = useState(null); // null = loading
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/assignments`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la lista");
      setRows([]);
    }
  };

  useEffect(() => {
    if (open && courseId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId]);

  const unassign = async (assignmentId) => {
    if (!window.confirm("¿Desasignar a este alumno? Perderá el acceso al curso/taller.")) return;
    setBusyId(assignmentId);
    try {
      await api.delete(`/admin/courses/${courseId}/assignments/${assignmentId}`);
      toast.success("Alumno desasignado");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo desasignar");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="course-assignments-modal">
        <DialogHeader>
          <DialogTitle>Asignados: {courseTitle}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          {rows === null ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Cargando…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">Nadie tiene este curso/taller asignado.</p>
          ) : (
            <table className="w-full text-sm" data-testid="course-assignments-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                  <th className="text-left py-2 pr-2">Alumno</th>
                  <th className="text-left py-2 px-2">Asignado el</th>
                  <th className="text-left py-2 px-2">Última actividad</th>
                  <th className="text-left py-2 px-2">Estado</th>
                  <th className="text-right py-2 pl-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.assignment_id} className="border-b border-gray-100" data-testid={`assignment-row-${r.assignment_id}`}>
                    <td className="py-2 pr-2">
                      <p className="text-brand-ink font-medium">{r.user.name || `${r.user.first_name || ""} ${r.user.last_name || ""}`.trim() || r.user.id}</p>
                      <p className="text-xs text-gray-500">{r.user.email}</p>
                    </td>
                    <td className="py-2 px-2 text-gray-700">{fmtDate(r.assigned_at)}</td>
                    <td className="py-2 px-2 text-gray-700">{fmtDate(r.last_attempt_at)}</td>
                    <td className="py-2 px-2">
                      {r.active ? (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                          Activo
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                          Finalizado
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right space-x-1 whitespace-nowrap">
                      <Link to={`/admin/users?open=${r.user.id}`}>
                        <Button variant="ghost" size="sm" title="Ir al alumno" data-testid={`go-to-user-${r.assignment_id}`}>
                          <UserRound className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                      {canUnassign && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Desasignar"
                          disabled={busyId === r.assignment_id}
                          onClick={() => unassign(r.assignment_id)}
                          data-testid={`unassign-${r.assignment_id}`}
                        >
                          <UserX className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
